/**
 * pricingJsonLd — the Product / Offer JSON-LD a pricing page emits for the app catalog (APP-ENTITLEMENT-001
 * Phase 11; MSEO-001: every JSON-LD blob a marketing site emits is built in `lib/esm/seo/`).
 *
 * One `Product` per app the catalog offers, each with ONE `Offer` whose `UnitPriceSpecification` carries the
 * published MONTHLY unit price of the app's first size dimension (or, for an app priced by blocks alone, its block
 * price), the unit it is priced by, and the currency. A free app is offered at 0 with its description; a
 * share-of-recovery app carries no price (it has none to publish) and is described instead; an app with no
 * published price, and an anchor marked coming soon, emit nothing — a crawler is never told a price nobody can pay.
 *
 * Every amount is the catalog's; every word arrives from the caller (translated), so this module holds neither.
 *
 * @module @puffin-core/marketing-shell/seo/pricingJsonLd
 */

import {
  PRICING_MODEL,
  consumablesOf,
  isPriced,
  monthlyUnitPriceOf,
  sizeDimensionsOf,
} from '@puffin-core/app-catalog';

const SCHEMA_CONTEXT = 'https://schema.org';
const IN_STOCK = 'https://schema.org/InStock';

/** Cents → the decimal string schema.org expects (`'129.95'`). */
export function schemaPrice(cents) {
  if (!Number.isInteger(cents) || cents < 0) {
    throw new TypeError(
      `schemaPrice: cents must be a non-negative integer, got ${JSON.stringify(cents)}`,
    );
  }
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
}

function offerOf({ priceCents, currency, unitText, url }) {
  const offer = {
    '@type': 'Offer',
    price: schemaPrice(priceCents),
    priceCurrency: currency,
    availability: IN_STOCK,
    url,
  };
  if (unitText) {
    offer.priceSpecification = {
      '@type': 'UnitPriceSpecification',
      price: schemaPrice(priceCents),
      priceCurrency: currency,
      unitText,
    };
  }
  return offer;
}

/**
 * The priced dimension a per-unit app is listed by: its first size dimension, else its first block. Called only for
 * a PRICED entry (`isPriced`), which has at least one of the two.
 *
 * @param {Object} entry - a public catalog row.
 * @returns {Object}
 */
function listedDimensionOf(entry) {
  const sizes = sizeDimensionsOf(entry);
  return sizes.length > 0 ? sizes[0] : consumablesOf(entry)[0];
}

/**
 * @typedef {Object} PricingJsonLdInput
 * @property {{apps: Object[]}} catalog - the public catalog.
 * @property {string} pageUrl - the pricing page's canonical URL (every offer's `url`).
 * @property {(dimension: Object) => string} unitTextOf - the words for a dimension's unit ("per location a month").
 * @property {(entry: Object) => string} describe - one sentence for an app (its tagline, or "free with any
 *   anchor", or the share it earns).
 * @property {string} [currency='USD']
 */

/**
 * The Product nodes for a pricing page.
 *
 * @param {PricingJsonLdInput} input
 * @returns {Object[]}
 */
export function pricingJsonLd({ catalog, pageUrl, unitTextOf, describe, currency = 'USD' }) {
  if (!catalog || !Array.isArray(catalog.apps)) {
    throw new TypeError('pricingJsonLd: `catalog.apps` (array) is required');
  }
  if (typeof pageUrl !== 'string' || pageUrl.length === 0) {
    throw new TypeError('pricingJsonLd: `pageUrl` (string) is required');
  }
  if (typeof unitTextOf !== 'function' || typeof describe !== 'function') {
    throw new TypeError('pricingJsonLd: `unitTextOf` and `describe` (functions) are required');
  }
  const nodes = [];
  for (const entry of catalog.apps) {
    if (!entry.isOffered || entry.comingSoon) continue;
    const node = {
      '@context': SCHEMA_CONTEXT,
      '@type': 'Product',
      name: entry.label,
      description: describe(entry),
    };
    if (entry.pricingModel === PRICING_MODEL.PER_UNIT) {
      if (!isPriced(entry)) continue;
      const dimension = listedDimensionOf(entry);
      node.offers = offerOf({
        priceCents: monthlyUnitPriceOf(dimension),
        currency,
        unitText: unitTextOf(dimension),
        url: pageUrl,
      });
    } else if (entry.pricingModel === PRICING_MODEL.INCLUDED_WITH_ANCHOR) {
      node.offers = offerOf({ priceCents: 0, currency, unitText: null, url: pageUrl });
    }
    nodes.push(node);
  }
  return nodes;
}

export default pricingJsonLd;
